import React, { useEffect, useState } from 'react';

import useCurrentUser from '../../hooks/useCurrentUser';
import Card from '../common/Card';
import { dateToChatMessageTime, dateToShortReadable } from '../../utils/dateUtils';
import { EInvitationStatus, Invitation } from '../../data_models/invitation';
import { ITask } from '../../data_models/task';
import useTasks from '../../hooks/useTasks';
import LoadingIndicator from '../common/LoadingIndicator';
import { Chip } from '../common/Chip';
import { FlatButton, OutlinedButton } from '../common/Button';
import useInvitations from '../../hooks/useInvitations';
import useChat from '../../hooks/useChat';
import useDisplayNotifications from '../../hooks/useDisplayNotifications';
import { DisplayNotificationType } from '../../models/DisplayNotification';
import LabelValuePair from '../common/LabelValuePair';
import { ChatMessage, EChatMessageType } from '../../data_models/chat';
import { EUserType } from '../../data_models/enums';
import { IoCheckmarkDoneOutline } from 'react-icons/io5';
import useConfig from '../../hooks/useConfig';
import { useSocket } from '../../hooks/useSocket';
import useRouting from '../../hooks/useRouting';
import useMyProjects from '../../hooks/useMyProjects';
import RichText from '../common/RichText';

interface MessageInvitationComponentProps {
    message: ChatMessage;
}

const MessageInvitationComponent: React.FC<MessageInvitationComponentProps> = ({ message }) => {

    const { currentUser, currentUserType } = useCurrentUser();
    const senderIsCurrentUser = message.sender.id === currentUser?._id && message.sender.userType === currentUserType;
    const readByPartner = message.readByUsersAt.some(readBy => readBy.userId.id !== currentUser?._id || readBy.userId.userType !== currentUserType);

    const { fetchTaskById } = useTasks();
    const { withdrawInvitation,
        acceptInvitation,
        declineInvitation,
        fetchInvitationById,
        markInvitationAsSeenByFreelancer } = useInvitations();
    const { goToProjectDetails } = useRouting();
    const { socket, listenToInvitations } = useSocket();
    const { config } = useConfig();
    const { getProjectById } = useMyProjects();

    const [isHovered, setIsHovered] = useState(false);

    const { addNotification } = useDisplayNotifications();

    const [invitation, setInvitation] = useState<Invitation>();
    const [task, setTask] = useState<ITask>();
    const [loading, setLoading] = useState(false);
    const [canSeeDetails, setCanSeeDetails] = useState(false);
    const receiveInvitationUpdateCallback = async (invitation: Invitation) => {
        if (invitation._id !== message.referenceId) return;
        setInvitation(invitation);
    };


    useEffect(() => {

        const fetchData = async () => {
            if (!message.referenceId || message.type !== EChatMessageType.INVITATION) return;
            try {
                setLoading(true);
                // fetch invitation
                const invitation = await fetchInvitationById(message.referenceId);
                setInvitation(invitation);
                // mark as seen if user is the invited freelancer and not seen yet
                if (invitation.freelancerId === currentUser?._id
                    && currentUserType === EUserType.FREELANCER
                    && !invitation.seenByFreelancer) {
                    await markInvitationAsSeenByFreelancer(invitation._id!);
                }

                // fetch corresponding task
                const task = await fetchTaskById(invitation.taskId) as ITask;
                setTask(task);

                // a user can see project details if they are participating as pm, freelancer or employer 
                const project = getProjectById(task.projectId || '');
                if (project) {
                    setCanSeeDetails(true);
                }
            } catch (error: any) {
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        try {
            if (currentUser && currentUserType) {
                listenToInvitations(receiveInvitationUpdateCallback);

                // Cleanup function to remove the listener
                return () => {
                    socket?.off("receive_invitation", receiveInvitationUpdateCallback);
                };
            }
        } catch (error) {
        }
    }, [
        currentUser,
        currentUserType,
        listenToInvitations,

        socket,
    ]);

    const onWithdraw = async () => {
        if (loading) return;
        try {
            setLoading(true);
            const updatedInvitation = await withdrawInvitation(invitation?._id!);
            setInvitation(updatedInvitation);
            addNotification({
                message: 'Invitation successfully withdrawn',
                type: DisplayNotificationType.SUCCESS
            })
        } catch (error: any) {
            addNotification({
                message: 'Invitation could not be withdrawn',
                type: DisplayNotificationType.ERROR
            })
        } finally {
            setLoading(false);
        }
    }

    const onSeeDetails = async () => {
        goToProjectDetails(invitation?.projectId!);
    }

    const onAccept = async () => {
        if (loading) return;
        try {
            setLoading(true);
            const result = await acceptInvitation(invitation?._id!);
            setInvitation(result.invitation);
            addNotification({
                message: 'Invitation successfully accepted',
                type: DisplayNotificationType.SUCCESS
            });
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            })
        } finally {
            setLoading(false);
        }
    }

    const onDecline = async () => {
        if (loading) return;
        try {
            setLoading(true);
            const updatedInvitation = await declineInvitation(invitation?._id!);
            setInvitation(updatedInvitation);
            addNotification({
                message: 'Invitation successfully declined',
                type: DisplayNotificationType.SUCCESS
            });
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            })
        } finally {
            setLoading(false);
        }
    }


    return (
        <div className={`flex ${senderIsCurrentUser ? 'justify-end ml-10' : 'justify-start mr-10'}`}>
            {loading
                ? <div><LoadingIndicator /></div>
                : <div className='px-2 '>
                    <div className={`min-w-64 
                    ${invitation?.status === EInvitationStatus.PENDING
                            ? 'shadow-lg'
                            : ''}
                    ${senderIsCurrentUser
                            ? 'bg-primary-100 px-4 py-2 rounded-bl-xl rounded-t-xl'
                            : ' bg-secondary-800 px-4 py-2 rounded-br-xl rounded-t-xl'}`}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        {/* Header */}
                        <div className={`
                        border-b mb-2 flex flex-row justify-between
                        ${senderIsCurrentUser
                                ? 'border-black'
                                : 'text-white border-white'}`}>
                            {/* Title */}
                            {senderIsCurrentUser && <p className='font-bold caption2'>{
                                task?.currentlyAssignedTo === invitation?.freelancerId 
                                ? 'Offer Accepted - Assigned'
                                : invitation?.status === EInvitationStatus.PENDING ?
                                    'Offer Sent'
                                    : invitation?.status === EInvitationStatus.ACCEPTED
                                        ? 'Offer Accepted'
                                        : invitation?.status === EInvitationStatus.REJECTED
                                            ? 'Offer Rejected'
                                            : invitation?.status === EInvitationStatus.WITHDRAWN
                                                ? 'Offer Withdrawn'
                                                : invitation?.status === EInvitationStatus.CANCELLED
                                                    ? 'Offer Cancelled'
                                                    : invitation?.status === EInvitationStatus.EXPIRED
                                                        ? 'Offer Expired'
                                                        : invitation?.status === EInvitationStatus.TASK_COMPLETED
                                                            ? 'Offer obsolete - Task completed'
                                                            : 'Offer Sent'}</p>}

                            {!senderIsCurrentUser && <p className='font-bold caption2'>{
                                task?.currentlyAssignedTo === currentUser?._id ?
                                    'Offer Accepted - Assigned'
                                    : invitation?.status === EInvitationStatus.PENDING ?
                                        'Offer Received'
                                        : invitation?.status === EInvitationStatus.ACCEPTED
                                            ? 'Offer Accepted'
                                            : invitation?.status === EInvitationStatus.REJECTED
                                                ? 'Offer Rejected'
                                                : invitation?.status === EInvitationStatus.WITHDRAWN
                                                    ? 'Offer Withdrawn'
                                                    : invitation?.status === EInvitationStatus.CANCELLED
                                                        ? 'Offer Cancelled'
                                                        : invitation?.status === EInvitationStatus.EXPIRED
                                                            ? 'Offer Expired'
                                                            : invitation?.status === EInvitationStatus.TASK_COMPLETED
                                                                ? 'Offer obsolete - Task completed'
                                                                : 'Offer Received'}</p>}
                            {/* Payout */}
                            <p className='caption2'>
                                {(senderIsCurrentUser && currentUserType === EUserType.EMPLOYER)
                                    ? `Credits: ${invitation?.payout}`
                                    : `Credits: ${invitation?.payout} ($${(invitation?.payout || 0) * (config?.rateCreditsToUsd.freelancer || 0)})`}
                            </p>
                        </div>

                        {/* Body */}
                        <div className={`
                        flex flex-col
                        ${senderIsCurrentUser
                                ? 'border-black'
                                : 'text-white border-white'}`}>
                            <p className='caption2'>{task?.title}</p>
                            <RichText text={task?.briefDescription || ''} />

                            <div className='mt-2 flex flex-row'>
                                {(task?.requiredSkills?.length || task?.requiredTools?.length || 0) > 0 && <p className='caption2 font-bold mr-1'>Skills/Tools asked:</p>}
                                {(task?.requiredSkills?.length || task?.requiredTools?.length || 0) > 0 && <p className='caption2'>{[...(task?.requiredSkills || []), ...(task?.requiredTools || [])].join(', ')}</p>}

                            </div>
                        </div>

                        {/* Actions */}
                        <div className='mt-2'>
                            {senderIsCurrentUser && <div className='flex flex-wrap items-center justify-end w-full gap-x-2 gap-y-1'>
                                {invitation?.status === EInvitationStatus.PENDING
                                    &&
                                    <OutlinedButton
                                        size='small'
                                        onClick={onWithdraw}
                                        isLoading={loading}
                                    >
                                        Withdraw
                                    </OutlinedButton>
                                }
                                {canSeeDetails && <OutlinedButton
                                    size='small'
                                    onClick={onSeeDetails}
                                    isLoading={loading}
                                >
                                    See Details
                                </OutlinedButton>}

                            </div>}
                            {!senderIsCurrentUser && <div className='flex flex-wrap items-center justify-end w-full gap-x-2 gap-y-1'>
                                {invitation?.status === EInvitationStatus.PENDING
                                    && <OutlinedButton
                                        size='small'
                                        onClick={onDecline}
                                        isLoading={loading}
                                    >
                                        Decline
                                    </OutlinedButton>}
                                {invitation?.status === EInvitationStatus.PENDING && <OutlinedButton
                                    size='small'
                                    onClick={onAccept}
                                    isLoading={loading}
                                >
                                    Accept
                                </OutlinedButton>}

                                {canSeeDetails && <OutlinedButton
                                    size='small'
                                    onClick={onSeeDetails}
                                    isLoading={loading}
                                >
                                    See Details
                                </OutlinedButton>}
                            </div>}
                        </div>

                    </div>

                    {/* Read and time section */}
                    <div className={`flex flex-row space-x-2 items-center min-w-28`}>
                        {(isHovered && readByPartner) ?
                            <p className={`text-xs ${senderIsCurrentUser ? 'ml-auto' : ''}`}>Read {dateToChatMessageTime(message?.readByUsersAt[0].readAt?.toLocaleString())} </p>
                            : <p className={`text-xs ${senderIsCurrentUser ? 'ml-auto' : ''}`}>{dateToChatMessageTime(message?._createdAt?.toLocaleString())} </p>
                        }

                        {!isHovered && senderIsCurrentUser && readByPartner &&
                            <p className='text-xs text-primary'>
                                <IoCheckmarkDoneOutline />
                            </p>}
                    </div>
                </div>
            }

        </div>
    );
};

export default MessageInvitationComponent;